import cx from 'classnames';
import { GovFormWeb } from '@gov-design-system-ce/react-form';
import type { FormDefinition } from '@gov-design-system-ce/react-form-core';
import type { EmailFormData } from '@gov-nx/core/hooks';
import { CommunicationType } from '@gov-nx/core/hooks';
import {
	useAuthorizedLabel,
	useTranslationWithNamespace,
} from '@gov-nx/core/service';
import type { PageCode } from '@gov-nx/module/page';
import {
	Button,
	FormInput,
	FormWidthElementLayout,
	LayoutGap,
} from '@gov-nx/ui/web';
import { SouhlasCheckbox } from '../../../Autorizace/SouhlasCheckbox';

interface ContactDataEmailFormProps {
	code: PageCode;
	formDefinition: FormDefinition<EmailFormData>;
	onSubmit: () => void;
	onChange: () => void;
	isNeedForVerification: boolean;
	isLowAuthorization: boolean;
	isAuthorizationEnforced: boolean;
	isUpdating: boolean;
	resendVerification?: () => void;
	isVerificationResending: boolean;
}

export const ContactEmailForm = ({
	code,
	formDefinition,
	onSubmit,
	onChange,
	isNeedForVerification,
	isLowAuthorization,
	isAuthorizationEnforced,
	resendVerification,
	isUpdating,
	isVerificationResending,
}: ContactDataEmailFormProps) => {
	const { getLocalizeCurried } = useTranslationWithNamespace();
	const tsn = getLocalizeCurried(code);
	const authLabel = useAuthorizedLabel();

	const type = CommunicationType.EMAIL;

	return (
		<GovFormWeb
			onSubmit={onSubmit}
			formDefinition={formDefinition}>
			<LayoutGap
				gap="m"
				className={cx('pb-6')}>
				<FormWidthElementLayout>
					<FormInput
						label={{ children: tsn('kontakt.pole.email') }}
						field={{
							name: 'email',
							isDisabled: isNeedForVerification || isUpdating,
						}}
					/>
					<p className="text-body-xs">{tsn('kontakt.pole.note.email')}</p>
				</FormWidthElementLayout>

				{isNeedForVerification ? (
					<FormWidthElementLayout>
						<FormInput
							label={{
								children: tsn('kontakt.pole.kod.' + type),
							}}
							field={{
								name: 'code',
								isDisabled: isUpdating,
							}}
						/>
						<p className="text-body-xs">
							{tsn('kontakt.pole.note.kod.' + type)}
						</p>
						{resendVerification && (
							<Button
								color="primary"
								type="link"
								isDisabled={isVerificationResending}
								isLoading={isVerificationResending}
								onClick={resendVerification}>
								{tsn('kontakt.akce.znovu-odeslat-email-kod')}
							</Button>
						)}
					</FormWidthElementLayout>
				) : null}

				<SouhlasCheckbox
					show={isLowAuthorization && isNeedForVerification}
					disabled={isUpdating}
				/>
				<div className="flex flex-col gap-4 mt-2 [ md:flex-row ]">
					<Button
						aria-label={authLabel(
							tsn(
								'kontakt.akce.wcag.' +
									type +
									(isNeedForVerification ? '.odeslat' : '.overit')
							),
							isAuthorizationEnforced
						)}
						nativeType="submit"
						color="primary"
						isDisabled={isUpdating}
						isLoading={isUpdating}
						size="m"
						type="solid">
						<span>
							{authLabel(
								tsn(
									'kontakt.akce.' +
										type +
										(isNeedForVerification ? '.odeslat' : '.overit')
								),
								isAuthorizationEnforced
							)}
						</span>
					</Button>
				</div>
				{isNeedForVerification && (
					<Button
						onClick={onChange}
						aria-label={tsn('kontakt.akce.wcag.' + type + '.zadat-jiny')}
						color="primary"
						type="link"
						size="m"
						className="mt-4">
						{tsn('kontakt.akce.' + type + '.zadat-jiny')}
					</Button>
				)}
			</LayoutGap>
		</GovFormWeb>
	);
};
